import React, { useState, useEffect } from 'react';
import { useDisclosure } from '@chakra-ui/hooks';
import { MdTrackChanges } from 'react-icons/md';
import { Bell, Plus, RotateCcw, Zap, Wifi, WifiOff } from 'lucide-react';
import GoBack from 'components/actions/GoBack';

import { useOKRStore } from './useOKRStore';
import { useOKRPermissions } from './useOKRPermissions';
import { Task, Objective } from './types';
import Dashboard from './components/Dashboard';
import ObjectivesPanel from './components/ObjectivesPanel';
import TaskBoard from './components/TaskBoard';
import TeamPanel from './components/TeamPanel';
import AlertsPanel from './components/AlertsPanel';
import CreateObjectiveModal from './components/CreateObjectiveModal';
import CreateTaskModal from './components/CreateTaskModal';
import WeeklyReviewPanel from './components/WeeklyReview';
import CapacityPlanning from './components/CapacityPlanning';
import FocusMode from './components/FocusMode';
import Templates from './components/Templates';
import OneOnOne from './components/OneOnOne';
import ActivityStats from './components/ActivityStats';
import { getAvailableMetrics } from 'interfaces/okr';

type Tab = 'dashboard' | 'objectives' | 'tasks' | 'focus' | 'review' | 'capacity' | 'team' | 'alerts' | 'templates' | 'activity';

const ALL_TABS: { id: Tab; label: string; ownerOnly?: boolean; requiresPilotageOkr?: boolean; badge?: (s: any) => number }[] = [
    { id: 'dashboard', label: 'Cockpit' },
    { id: 'objectives', label: 'Objectifs' },
    { id: 'tasks', label: 'Tâches', badge: (s) => s.tasks.filter((t: Task) => t.status === 'blocked').length },
    { id: 'focus', label: 'Focus' },
    { id: 'review', label: 'Revue hebdo', ownerOnly: true },
    { id: 'capacity', label: 'Capacité', ownerOnly: true },
    { id: 'team', label: 'Équipe & 1:1', ownerOnly: true },
    { id: 'alerts', label: 'Alertes', ownerOnly: true, badge: (s) => s.alerts.filter((a: any) => !a.acknowledged).length },
    { id: 'templates', label: 'Templates', ownerOnly: true },
    // Commercial call/email/meeting counters — only ever shown when the OKR
    // option is activated for this owner (same pilotageOkr flag the sidebar
    // link already relies on); the backend independently 403s the endpoint too.
    { id: 'activity', label: 'Activités commerciales', ownerOnly: true, requiresPilotageOkr: true },
];

const OKRModule: React.FC = () => {
    const [tab, setTab] = useState<Tab>('dashboard');
    const { canManageObjectives } = useOKRPermissions();
    const pilotageOkr = localStorage.getItem('pilotageOkr') === 'true';
    // ownerOnly tabs unlock for the owner or for a sub-account granted manage:okr (see useOKRPermissions).
    const TABS = ALL_TABS.filter((t) => (!t.ownerOnly || canManageObjectives) && (!t.requiresPilotageOkr || pilotageOkr));
    const {
        state, loading, apiAvailable,
        addObjective, addTask, addActionPlan,
        editObjective, removeObjective,
        editKPI, removeKPI,
        editTask,
        updateTaskStatus, acknowledgeAlert, resetToDemo,
    } = useOKRStore();

    const [availableMetrics, setAvailableMetrics] = useState<any[]>([]);

    // Edit objective modal state
    const objModal = useDisclosure();
    const [editingObjective, setEditingObjective] = useState<Objective | undefined>(undefined);

    // Edit task modal state
    const taskModal = useDisclosure();
    const [editingTask, setEditingTask] = useState<Task | undefined>(undefined);
    const [newTaskObjectiveId, setNewTaskObjectiveId] = useState<string>('');

    useEffect(() => {
        getAvailableMetrics().then((m) => { if (m) setAvailableMetrics(m); });
    }, []);

    const critAlerts = state.alerts.filter((a) => !a.acknowledged && a.severity === 'critical').length;
    const blockedTasks = state.tasks.filter((t) => t.status === 'blocked').length;

    // ── Edit handlers ─────────────────────────────────────────────────────────

    const handleEditObjective = (obj: Objective) => {
        setEditingObjective(obj);
        objModal.onOpen();
    };
    const handleNewObjective = () => {
        setEditingObjective(undefined);
        objModal.onOpen();
    };

    const handleEditTask = (task: Task) => {
        setEditingTask(task);
        taskModal.onOpen();
    };
    const handleNewTask = (objectiveId?: string) => {
        setEditingTask(undefined);
        setNewTaskObjectiveId(objectiveId ?? '');
        taskModal.onOpen();
    };

    const handleObjModalClose = () => {
        setEditingObjective(undefined);
        objModal.onClose();
    };
    const handleTaskModalClose = () => {
        setEditingTask(undefined);
        setNewTaskObjectiveId('');
        taskModal.onClose();
    };

    return (
        <div className="mt-3 h-full w-full">
            <GoBack />

            {/* Page header */}
            <div className="mb-6 mt-2 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-lightPrimary text-3xl text-toola-500">
                        <MdTrackChanges />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold text-navy-700">Pilotage & OKR</h1>
                            {loading && <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-200 border-t-toola-500 animate-spin" />}
                            <span title={apiAvailable ? "Connecté à l'API" : "Mode démonstration"}>
                                {apiAvailable ? <Wifi size={13} className="text-green-400" /> : <WifiOff size={13} className="text-gray-300" />}
                            </span>
                        </div>
                        <p className="text-sm text-gray-500">Objectifs · KPIs · Revue hebdo · Capacité · Focus · 1:1</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    {critAlerts > 0 && (
                        <button onClick={() => setTab('alerts')} className="flex items-center gap-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 hover:bg-red-100 transition-colors">
                            <Bell size={14} />{critAlerts} critique{critAlerts > 1 ? 's' : ''}
                        </button>
                    )}
                    {blockedTasks > 0 && (
                        <button onClick={() => setTab('tasks')} className="flex items-center gap-1.5 text-sm font-medium text-orange-600 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2 hover:bg-orange-100 transition-colors">
                            <Zap size={14} />{blockedTasks} bloquée{blockedTasks > 1 ? 's' : ''}
                        </button>
                    )}
                    <button onClick={() => handleNewTask()} className="flex items-center gap-2 text-sm font-medium text-navy-700 bg-white border border-gray-200 rounded-xl px-4 py-2 hover:bg-gray-50 transition-colors">
                        <Plus size={15} /> Tâche rapide
                    </button>
                    <button onClick={handleNewObjective} className="linear flex items-center gap-2 rounded-xl bg-toola-500 px-4 py-2 text-sm font-medium text-white transition duration-200 hover:bg-toola-600 active:bg-toola-700">
                        <Plus size={15} /> Nouvel objectif
                    </button>
                    <button onClick={resetToDemo} title="Réinitialiser données démo" className="p-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-400 hover:text-gray-600 transition-colors">
                        <RotateCcw size={15} />
                    </button>
                </div>
            </div>

            {/* Tab bar — scrollable underline style */}
            <div className="overflow-x-auto">
                <div className="flex gap-6 border-b border-gray-100 mb-6" style={{ minWidth: 'max-content' }}>
                    {TABS.map(({ id, label, badge }) => {
                        const count = badge ? badge(state) : 0;
                        return (
                            <button
                                key={id}
                                onClick={() => setTab(id)}
                                className={`relative pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                                    tab === id ? 'border-toola-500 text-toola-500' : 'border-transparent text-gray-500 hover:text-navy-700'
                                }`}
                            >
                                {label}
                                {count > 0 && (
                                    <span className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-xs font-bold text-white bg-red-500">
                                        {count > 9 ? '9+' : count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Content */}
            {tab === 'dashboard' && (
                <Dashboard state={state} onStatusChange={updateTaskStatus} onTabChange={(t) => setTab(t as Tab)} />
            )}
            {tab === 'objectives' && (
                <ObjectivesPanel
                    state={state}
                    onStatusChange={updateTaskStatus}
                    onEditObjective={handleEditObjective}
                    onDeleteObjective={removeObjective}
                    onEditKPI={editKPI}
                    onDeleteKPI={removeKPI}
                    onEditTask={handleEditTask}
                    onNewTask={handleNewTask}
                    onAddActionPlan={addActionPlan}
                    onUpdateHours={(id, hours) => editTask(id, { spentHours: hours })}
                />
            )}
            {tab === 'tasks' && (
                <div className="!z-5 relative flex flex-col rounded-[20px] bg-white bg-clip-border shadow-3xl shadow-shadow-500 p-6">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <h2 className="text-lg font-bold text-navy-700">Toutes les tâches</h2>
                            <p className="text-sm text-gray-500">{state.tasks.length} tâche{state.tasks.length > 1 ? 's' : ''} au total</p>
                        </div>
                        <button onClick={() => handleNewTask()} className="linear flex items-center gap-2 rounded-xl bg-toola-500 px-4 py-2 text-sm font-medium text-white hover:bg-toola-600">
                            <Plus size={14} /> Nouvelle tâche
                        </button>
                    </div>
                    <TaskBoard tasks={state.tasks} state={state} onStatusChange={updateTaskStatus} onEditTask={handleEditTask} onUpdateHours={(id, hours) => editTask(id, { spentHours: hours })} />
                </div>
            )}
            {tab === 'focus' && <FocusMode state={state} onStatusChange={updateTaskStatus} />}
            {tab === 'review' && <WeeklyReviewPanel state={state} onAddTask={() => handleNewTask()} />}
            {tab === 'capacity' && <CapacityPlanning state={state} />}
            {tab === 'team' && (
                <div className="space-y-8">
                    <TeamPanel state={state} />
                    <OneOnOne state={state} />
                </div>
            )}
            {tab === 'alerts' && <AlertsPanel state={state} onAcknowledge={acknowledgeAlert} />}
            {tab === 'activity' && <ActivityStats state={state} />}
            {tab === 'templates' && (
                <Templates
                    teamMembers={state.team.map((m) => ({ id: m.id, name: m.name }))}
                    onCreateObjective={(obj, kpis) => addObjective(obj, kpis)}
                />
            )}

            {/* Objective modal — create or edit */}
            <CreateObjectiveModal
                isOpen={objModal.isOpen}
                onClose={handleObjModalClose}
                onSubmit={(obj, kpis) => addObjective(obj, kpis)}
                onUpdate={editObjective}
                editObjective={editingObjective}
                teamMembers={state.team.map((m) => ({ id: m.id, name: m.name }))}
                availableMetrics={availableMetrics}
            />

            {/* Task modal — create or edit */}
            <CreateTaskModal
                isOpen={taskModal.isOpen}
                onClose={handleTaskModalClose}
                onSubmit={(task) => addTask({ ...task, objectiveId: task.objectiveId || newTaskObjectiveId })}
                onUpdate={editTask}
                editTask={editingTask}
                state={state}
            />
        </div>
    );
};

export default OKRModule;
